import React, { memo } from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { PersonOff, CheckCircleOutline } from '@mui/icons-material';
import { AbsentEmployeesData } from '../../types/dashboardTypes';
import {
    AccentAvatar,
    AccentChip,
    DashboardCard,
    DashboardEmptyState,
    DashboardListCardSkeleton,
    DashboardListRow,
    thinScrollbarSx,
    useDashboardTones,
} from './DashboardCard';

interface AbsentEmployeesCardProps {
    data: AbsentEmployeesData;
}

/**
 * Elegant Absent/On-Leave Employees Card
 * Shows employees who are absent or on leave today with refined aesthetics
 * Supports light and dark themes
 */
const AbsentEmployeesCard: React.FC<AbsentEmployeesCardProps> = memo(({ data }) => {
    const { ink } = useDashboardTones();
    const { absentCount, onLeaveCount, employees } = data;
    const totalMissing = absentCount + onLeaveCount;
    const headerChipSx = { fontWeight: 600, fontSize: 10, height: 24 };

    return (
        <DashboardCard
            accent="error"
            icon={PersonOff}
            title="Not in Today"
            subtitle="Employees absent or on leave"
            actions={
                <Stack direction="row" spacing={0.75}>
                    <AccentChip accent="error" variant="outlined" label={`${absentCount} Absent`} sx={headerChipSx} />
                    <AccentChip accent="caution" variant="outlined" label={`${onLeaveCount} Leave`} sx={headerChipSx} />
                </Stack>
            }
        >
            {/* Employee List */}
            <Box
                sx={{
                    flex: 1,
                    overflow: totalMissing > 0 ? 'auto' : 'hidden',
                    px: 1.5,
                    py: 1,
                    display: totalMissing === 0 ? 'flex' : 'block',
                    minHeight: totalMissing > 0 ? 160 : 'auto',
                    maxHeight: totalMissing > 0 ? 260 : 'none',
                    ...thinScrollbarSx(ink(0.1), ink(0.2)),
                }}
            >
                {totalMissing === 0 ? (
                    <DashboardEmptyState
                        accent="success"
                        icon={CheckCircleOutline}
                        title="All employees are present today"
                        description="Full team attendance"
                    />
                ) : (
                    <Stack spacing={0.75}>
                        {employees.map((employee) => {
                            const accent = employee.status === 'ABSENT' ? 'error' : 'caution';
                            return (
                                <DashboardListRow key={employee._id} interactive sx={{ gap: 1.5 }}>
                                    <AccentAvatar accent={accent} src={employee.profileImage}>
                                        {employee.name.charAt(0).toUpperCase()}
                                    </AccentAvatar>
                                    <Box flex={1} minWidth={0}>
                                        <Typography variant="body2" fontWeight={500} noWrap color="text.primary" fontSize={13}>
                                            {employee.name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" noWrap fontSize={11}>
                                            {employee.role}
                                        </Typography>
                                    </Box>
                                    <AccentChip
                                        accent={accent}
                                        variant="solid"
                                        label={employee.status === 'ABSENT' ? 'Absent' : 'On Leave'}
                                        sx={{ height: 20, fontSize: 10, fontWeight: 500 }}
                                    />
                                </DashboardListRow>
                            );
                        })}
                    </Stack>
                )}
            </Box>
        </DashboardCard>
    );
});

AbsentEmployeesCard.displayName = 'AbsentEmployeesCard';

export const AbsentEmployeesCardSkeleton: React.FC = memo(() => (
    <DashboardListCardSkeleton subtitle sx={{ height: '100%' }} />
));

AbsentEmployeesCardSkeleton.displayName = 'AbsentEmployeesCardSkeleton';

export default AbsentEmployeesCard;
